import React from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { conf, t } from '../domain/services/configService';
import { SmartLink } from './SmartLink';
import { theme } from '../domain/theme';
import { getContentDateData, getHubPage } from '../domain/utils/contentUtils';
import { PreHeadline } from './responsive/atoms/PreHeadline';
import { Headline } from './responsive/atoms/Headline';
import { SubHeadline } from './responsive/atoms/SubHeadline';
import { PhotographersLink } from './responsive/atoms/PhotographersLink';
import { getImageCreditPrefix } from '../domain/utils/imageEntityUtils';

export const ContentMainTitle = ({ content }) => {
  const hubPage = getHubPage(content);
  const authors =
    Array.isArray(content.authors) && content.authors.length > 0
      ? content.authors
      : [];
  const contentDateData = getContentDateData(content);

  const styleMainTit = css`
    padding: 20px 0;
    margin: 0 15px;
    ${theme.mq.tablet} {
      width: 91%;
      max-width: 1160px;
      margin: 0 auto;
    }
  `;

  const styleMainHlDeck = css`
    display: flex;
    flex-direction: column;
    margin-top: 20px;
    ${theme.mq.tablet} {
      flex-direction: row;
      margin-top: 25px;
    }
  `;
  const styleMainHlDate = css`
    ${theme.font.family('regularWeb')};
    font-size: 12px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: 2px;
    color: #555555;
    text-transform: uppercase;
    display: flex;
    flex-direction: column;
    ${theme.mq.small_desktop} {
      flex-direction: row;
      color: #000000;
      ${theme.font.family('regularWeb')};
      white-space: nowrap;
    }
  `;
  const styleMainHlDateSpan = css`
    ${theme.mq.tablet} {
      ${theme.font.family('boldWeb')};
      color: #000000;
    }
  `;
  const styleUpdated = css`
    display: block;
    ${theme.mq.tablet} {
      display: inline;
    }
  `;
  const styleHideOnHandheld = css`
    display: none;
    ${theme.mq.small_desktop} {
      display: inline;
    }
  `;
  const styleContributors = css`
    font-size: 12px;
    ${theme.font.family('boldWeb')};
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: 3px;
    color: #000000;
    text-transform: uppercase;
    ${theme.mq.tablet} {
      ${theme.font.family('regularWeb')};
      span {
        ${theme.font.family('boldWeb')};
        color: #000000;
      }
    }
    a {
      color: #000;
      &:hover {
        color: #666;
      }
    }
  `;
  const styleContributorsPhotographerBorder = css`
    ${theme.mq.tablet} {
      ${authors.length > 0 &&
      `border-left: 1px solid #999;
      margin-left: 10px;
      padding-left: 10px;`}
    }
  `;

  const styleAuthorLink = css`
    border-bottom: 2px solid #fc0;
  `;

  const immersiveLeadContent = content.immersiveLead?.entity;

  return (
    <div css={styleMainTit}>
      {!immersiveLeadContent && (
        <>
          {hubPage && hubPage.url && hubPage.title && (
            <PreHeadline>
              <SmartLink hrefLang={conf.langcode} to={hubPage.url}>
                {hubPage.title}
              </SmartLink>
            </PreHeadline>
          )}
          <Headline>{content.title}</Headline>
          {content.subHeadline && (
            <SubHeadline>{content.subHeadline}</SubHeadline>
          )}
        </>
      )}
      <div css={styleMainHlDeck}>
        {authors.length > 0 && (
          <div css={[styleContributors]}>
            {`${t('By')} `}
            <span>
              {authors
                .map(author => {
                  if (!author.entity) {
                    return;
                  }
                  const authorPageUrl =
                    author.entity?.numberOfPublishedNodes?.count > 1
                      ? author.entity?.path?.alias
                      : undefined;
                  return authorPageUrl ? (
                    <SmartLink
                      to={authorPageUrl}
                      css={styleAuthorLink}
                      key={author.entity.name}
                    >
                      {author.entity.name}
                    </SmartLink>
                  ) : (
                    author.entity.name
                  );
                })
                // oxlint-disable-next-line no-array-reduce
                .reduce((result, author) => [result, ', ', author])}
            </span>
          </div>
        )}
        {content.fieldTermPhotographer ? (
          <div css={[styleContributors, styleContributorsPhotographerBorder]}>
            <PhotographersLink
              photographers={content.fieldTermPhotographer.map(
                (photographer, index) => {
                  const thisPhotographer = photographer;
                  if (index === 0) {
                    thisPhotographer.entity.prefix = getImageCreditPrefix(
                      photographer.entity.creditPrefix,
                      true
                    );
                  }
                  return thisPhotographer;
                }
              )}
              color={{ text: '#000', link: '#000' }}
            />
          </div>
        ) : undefined}
      </div>
      {contentDateData && (
        <div css={styleMainHlDate}>
          <span>
            {`${t('__PUBLISHED__')} `}
            <span css={styleMainHlDateSpan}>{contentDateData.published}</span>
          </span>
          {contentDateData.updated && (
            <span css={styleUpdated}>
              <span css={styleHideOnHandheld}>, </span>
              {`${t('__UPDATED__')} `}
              <span css={styleMainHlDateSpan}>{contentDateData.updated}</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

ContentMainTitle.propTypes = {
  content: PropTypes.objectOf(PropTypes.any).isRequired
};
